import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { createManyTrips, deleteAllTrips } from '../../moduleServices/travel_services';

// ###### Normalize Excel and date input
const normalizeDate = (value) => {
  if (!value) return '';
  let dateObj;
  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    dateObj = new Date(excelEpoch.getTime() + value * 86400000);
  } else {
    dateObj = new Date(value);
  }
  return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : '';
};

// ###### MAIN COMPONENT
const CardExportImport = ({ trips = [], fetchTrips }) => {
  const fileInputRef = useRef(null);
  const [importMode, setImportMode] = useState('append');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setParsedData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ###### Handle Export with serialized places
  const handleExport = () => {
    if (!Array.isArray(trips) || !trips || typeof trips.length === 'undefined') {
      toast.error('No trips data available to export');
      return;
    }

    if (trips.length === 0) {
      toast.error('No trips to export');
      return;
    }

    const flattenedRows = trips.flatMap((trip) => { // Flatten trips with places into multiple rows
      const baseTrip = {
        id: trip.id,
        title: trip.title,
        description: trip.description,
        start_date: trip.start_date,
        end_date: trip.end_date,
        google_photos_url: trip.google_photos_url,
      };

      if (Array.isArray(trip.places) && trip.places.length > 0) {
        return trip.places.map((place) => ({
          ...baseTrip,
          'places.id': place.id,
          'places.country': place.country,
          'places.name': place.name,
          'places.arrival_date': place.arrival_date,
          'places.departure_date': place.departure_date,
          'places.latitude': place.latitude,
          'places.longitude': place.longitude,
          'places.google_place_id': place.google_place_id,
          'places.google_maps_url': place.google_maps_url,
        }));
      } else {
        return [{ ...baseTrip }];
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(flattenedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'TripsWithPlaces');
    XLSX.writeFile(workbook, 'trips_export.xlsx');
  };

  
  // ###### Handle file selection and parse flattened Excel content
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet);

        // Group trips by trip ID and collect places per trip
        const tripMap = new Map();

        data.forEach((row) => {
          const tripId = row.id;

          if (!tripMap.has(tripId)) {
            tripMap.set(tripId, {
              id: row.id,
              title: row.title,
              description: row.description,
              start_date: normalizeDate(row.start_date),
              end_date: normalizeDate(row.end_date),
              google_photos_url: row.google_photos_url,
              places: [],
            });
          }

          const hasPlaceData = row['places.id'] || row['places.name'] || row['places.country'];

          if (hasPlaceData) {
            tripMap.get(tripId).places.push({
              id: row['places.id'],
              country: row['places.country'],
              name: row['places.name'],
              arrival_date: normalizeDate(row['places.arrival_date']),
              departure_date: normalizeDate(row['places.departure_date']),
              latitude: row['places.latitude'],
              longitude: row['places.longitude'],
              google_place_id: row['places.google_place_id'],
              google_maps_url: row['places.google_maps_url'],
            });
          }
        });

        const groupedTrips = Array.from(tripMap.values());
        setParsedData(groupedTrips);
        toast.success('File loaded successfully');
      } catch (err) {
        console.error(err);
        toast.error('Failed to read file');
        resetState();
      }
    };

    reader.readAsBinaryString(file);
  };

  // ###### Confirm override and import
  const proceedImport = async () => {
    setShowConfirmModal(false);
    setIsImporting(true);
    try {
      await deleteAllTrips();
      await createManyTrips(parsedData);
      toast.success('Imported successfully (override)');
      resetState();
      if (fetchTrips) fetchTrips();
    } catch (err) {
      console.error(err);
      toast.error('Error importing trips');
    } finally {
      setIsImporting(false);
    }
  };

  // ###### Handle Import button click
  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('No data to import');
      return;
    }

    if (importMode === 'override') {
      setShowConfirmModal(true);
      return;
    }

    try {
      setIsImporting(true);
      await createManyTrips(parsedData);
      toast.success('Imported successfully (append)');
      resetState();
      if (fetchTrips) fetchTrips();
    } catch (err) {
      console.error(err);
      toast.error('Error importing trips');
    } finally {
      setIsImporting(false);
    }
  };

  const buttonBase = 'px-6 py-3 rounded-xl shadow-md text-white';
  const importDisabled = 'bg-gray-400 cursor-not-allowed';
  const importEnabled = 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <>
      <div className="h-auto bg-white shadow-lg border border-dashed border-gray-300 rounded-xl p-4 flex flex-col gap-4">
        <span className="text-md font-semibold">Export and Import</span>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleExport}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md"
          >
            Export Trips to Excel
          </button>

          <label className="flex-1 cursor-pointer bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-md text-center">
            Select Excel to Import
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>

        <div className="text-sm text-gray-700">
          Uploaded file: <strong>{selectedFile?.name || 'none'}</strong>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-gray-700 font-semibold">Import Mode:</label>
            <select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              className="border p-2 rounded-xl shadow-sm"
            >
              <option value="append">Append</option>
              <option value="override">Override</option>
            </select>
          </div>

          <button
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
            className={`${buttonBase} ${
              !selectedFile || isImporting ? importDisabled : importEnabled
            }`}
          >
            {isImporting ? 'Importing...' : 'Perform Import'}
          </button>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm override</h3>
            <p className="mb-6 text-sm text-gray-700">
              This will delete <strong>all existing trips</strong> before importing the new ones. Are you sure?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={proceedImport}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardExportImport;